<script setup lang="ts">
const router = useRouter();
const route = useRoute();
import api from "~/utils/auth/api";
import { useQuery } from "@tanstack/vue-query";
import { signalREndpoints, useSignalR } from "~/composables/SignalR/useSignalR";
import BetbookSignalRCallback from "~/composables/SignalR/callbacks/BetbookSignalRCallback";
import { mdiChevronLeft } from "@mdi/js";
import { IPrice } from "~/types/sports/price";
import { IEvent } from "~/types/sports/event";
import { IMarket } from "~/types/sports/market";
import { IOutcome } from "~/types/sports/outcome";
import { IMarketGroup } from "~/types/racing/market-group";
import RegionFlag from "~/components/layout/TrackRacing/Shared/Region/Flag.vue";
import t from "~/utils/translate";
import { useBetbook } from "~/store/betbook";
import MultiMarketsOutcomes from "~/components/layout/TrackRacing/Shared/Outcome/MultiMarketsOutcomes.vue";
import Outright from "~/components/layout/TrackRacing/Shared/Outright/Outright.vue";
import Navigation from "~/components/layout/TrackRacing/Shared/Event/Navigation.vue";
import MarketNav from "~/components/layout/TrackRacing/Shared/Market/MarketNav.vue";
import ResultsIcon from "~/components/layout/TrackRacing/Shared/ResultsIcon.vue";
import TimeForm from "~/components/layout/TrackRacing/Shared/Event/TimeForm.vue";
import { IRaceDetails } from "~/types/racing/race-details";
import useRacing from "~/composables/TrackRacing/useRacing";
import { isMobile } from "~/utils/constants";
import { useSettings } from "~/store/settings";
import useSignalRRecconect from "~/composables/useSignalRRecconect";

const { convertDistance } = useRacing();
interface Props {
	selectedEventId: number | undefined;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	dailyRaceEvents?: any[];
	showRelatedEventsNav?: boolean | true;
	isOutright?: boolean;
	isSpecialEvent?: boolean;
}

export interface IResults {
	events: IEvent[];
	marketGroups: IMarketGroup[];
	markets: IMarket[];
	outcomes: IOutcome[];
	prices: IPrice[];
}

export interface IRaceEvent {
	result: IResults;
	id: number;
	status: number;
	isCanceled: boolean;
	isCompleted: boolean;
	isCompletedSuccessfully: boolean;
	creationOptions: number;
	isFaulted: boolean;
}

const config = useRuntimeConfig();
const settings = useSettings();
const props = defineProps<Props>();
const emit = defineEmits(["handle-auto-scroll"]);
const isStartingPrice = ref(false);
const selectedMarketGroup = ref("");
const betbookSignalR = useSignalR("betBook", BetbookSignalRCallback);
const abortController = new AbortController();
const { $checkSignalRConnectionStatus } = useNuxtApp();

const selectedEventId = computed(() => props.selectedEventId);

const store = useBetbook();
const event = computed(() => (selectedEventId.value ? store.getEvent(Number(selectedEventId.value)) : ({} as IEvent)));
const eventType = computed(() => {
	return `${route.query.eventType}`;
});

const regionId = computed(() => {
	return route.query.regionId;
});

const league = computed(() => {
	const copy: { [x: string]: string } = {};
	const query = Object.assign(copy, route.query);
	return query.league;
});

const relateEvents = computed(() => {
	const region = props.dailyRaceEvents?.find((r) => r.id === regionId.value);
	const events = league.value ? region?.leagues[league.value] : [];
	return props.isSpecialEvent ? props.dailyRaceEvents : events;
});

const handleSubscription = async (markets: IMarket[]) => {
	await $checkSignalRConnectionStatus("TrackRacing");
	const uniqueMarkets = new Set(markets.map((x) => x.name));
	betbookSignalR.subscribe(signalREndpoints.subscriptionBetbookEndpoint, [Number(selectedEventId.value)], {
		eventIds: [Number(selectedEventId.value)],
		marketTypes: [...uniqueMarkets].map((x) => x),
		countryCode: settings.config?.appSetting?.CountryCode,
	});
};

const raceEventApiCall = async () => {
	const response = await api<IRaceEvent>({
		url: `/TrackRacing/GetEvent?eventIds=${props.selectedEventId}&marketGroupName=${selectedMarketGroup.value}&countryCode=${settings.config?.appSetting?.CountryCode}`,
		type: "horses",
		apiVersion: "v1",
		signal: abortController.signal,
		options: { method: "GET" },
	});
	isStartingPrice.value = response.result.markets.some(
		(market) =>
			market.name.includes("Race Winner") && market?.additionalInfo?.UseFixedPrice === "False" && market?.additionalInfo?.UseStartingPrice === "True"
	);
	const marketsExcludingNonRunnerNoBet = response.result.markets.filter((market) => market.name !== "[Ante Post Race Non Runner No Bet]");
	const markets = selectedMarketGroup.value
		? marketsExcludingNonRunnerNoBet.filter(
				(m) => m.additionalInfo?.MarketGroupName === selectedMarketGroup.value.toLowerCase().replaceAll(" ", "-").replaceAll("/", "or")
		  )
		: marketsExcludingNonRunnerNoBet;

	handleSubscription(markets);

	await Promise.all([
		store.setEvents(response.result.events),
		store.setMarkets(markets),
		store.setOutcomes(response.result.outcomes),
		store.setPrices(response.result.prices),
	]);
	if (event.value.isActive && selectedMarketGroup.value === "") {
		refetchUkConfig();
	}

	return response;
};

const {
	data: selectedEvent,
	isLoading,
	isFetching,
	refetch: refetchEvent,
} = useQuery<IRaceEvent>({
	queryKey: ["GetEventById", props.selectedEventId],
	refetchOnReconnect: true,
	queryFn: raceEventApiCall,
});

const { data: ukConfig, refetch: refetchUkConfig } = useQuery<IRaceDetails>({
	queryKey: ["race-config", props.selectedEventId],
	enabled: false,
	queryFn: () =>
		fetch(`${config.public.horsesEventDetails}/api/events/v2/GetRaceDetails`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				EventId: props.selectedEventId,
				ConfigurationSections: [14],
				languageId: 1,
				clientTypeId: 1,
				brandId: 8,
				jurisdictionId: 8,
				ClientIntegratorId: 1,
				scoreboardRequest: {
					scoreboardType: 3,
				},
				territoryId: 198,
			}),
		}).then((res) => res.json()),
});

const handleEventRefetch = (marketGroupName: string) => {
	selectedMarketGroup.value = marketGroupName;
	refetchEvent();
};

watch(selectedEventId, (newValue, oldValue) => {
	if (newValue !== oldValue) {
		selectedMarketGroup.value = "";
		refetchEvent();
	}
});

const ledger = useLedger();
const handleSignalRUnsubscribe = () => {
	const ledgerData = ledger.get("events", "betBook");
	if (ledgerData) {
		betbookSignalR.unSubscribe(signalREndpoints.unsubscriptionBetbookEndpoint, {
			eventIds: ledgerData.events,
			marketTypes: ledgerData.markets,
			countryCode: settings.config?.appSetting?.CountryCode,
		});
	}
};
onBeforeUnmount(() => {
	handleSignalRUnsubscribe();
});

const handleReconnect = () => {
	handleSignalRUnsubscribe();
	refetchEvent();
};

useSignalRRecconect(handleReconnect);

const handleBackButton = (eventType: string) => {
	const { query } = route;
	const isNextOff = Boolean(query?.isNextOff);
	emit("handle-auto-scroll");
	router.push({ query: { eventType: isNextOff ? "nextOff" : eventType } });
};
const scrollToTop = () => {
	setTimeout(() => {
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}, 300);
};
onMounted(() => {
	scrollToTop();
});

onUnmounted(() => {
	abortController.abort();
});
</script>

<template>
	<div>
		<Navigation v-if="showRelatedEventsNav" :is-loading="isLoading && isFetching" :selected-event-id="selectedEventId" :events="relateEvents || []" />

		<div class="rounded-lg bg-dark-800 text-light-50 text-xs">
			<div class="flex items-center">
				<div
					@click="handleBackButton(eventType)"
					class="ml-3 mr-2 rounded-lg flex items-center justify-center flex-shrink-0 w-8 h-8 transition-all cursor-pointer"
				>
					<UiIcon :path="mdiChevronLeft" class="w-[25px] h-[25px] fill-light-50" />
				</div>
				<div class="mr-1 w-[1px] h-[32px] border-r border-r-dark-500" />
				<div class="rounded bg-dark-700 w-full m-1 p-1">
					<div v-if="isOutright || isSpecialEvent" class="leading-none flex items-center my-1 font-bold">
						{{ selectedEvent?.result.events[0].displayName }}
					</div>
					<div v-else class="leading-none flex items-center mb-1">
						<RegionFlag :region-id="selectedEvent?.result.events[0].regionId" :width="16" :height="16" class="mr-[2px]" />
						{{ selectedEvent?.result.events[0].league }}
					</div>
					<div class="flex items-center leading-none">
						<div v-if="!isOutright" class="bg-primary-50 font-bold text-dark-800 w-[max-content] rounded-md py-[2px] px-2 mr-1">
							{{ t("race") }} {{ selectedEvent?.result.events[0].sportSpecificProperties?.raceNumber }}
						</div>
						<UiDateTime
							v-if="selectedEvent?.result.events[0].expectedStartEpoch"
							dateFormat="dd MMM"
							timeFormat=" HH:mm"
							time
							:date="selectedEvent.result.events[0].expectedStartEpoch"
							string-separator=" -"
						/>
					</div>
				</div>
			</div>
			<div class="px-2 py-[3px] bg-dark-700 rounded-b-lg">
				<div class="flex items-center justify-between leading-tight">
					<div v-if="event.isFinished" class="flex items-center">
						<ResultsIcon />
						<div class="mx-1">{{ t("event-finished") }}</div>
					</div>
					<div v-else>
						<span class="font-bold">{{ t("going") }}</span> :

						<span v-if="ukConfig?.Race?.Going">{{ ukConfig.Race.Going }}</span>
						<span v-else>N/A</span>
						|
						<span class="font-bold">{{ t("distance") }}</span> :
						<span v-if="ukConfig?.Race?.Distance">{{ convertDistance(ukConfig.Race.Distance) }}</span>
						<span v-else>N/A</span>
					</div>
					<div class="h-[32px]">
						<div
							v-if="
								selectedEvent?.result.markets.some(
									(m) => m.additionalInfo?.UseStartingPrice === 'True' && m.additionalInfo.UseFixedPrice === 'True' && m.name.includes('Race Winner')
								) && selectedEvent.result.events[0].isActive
							"
							class="flex items-center"
						>
							<div class="mx-1 text-xs font-semibold">
								<span v-if="isMobile">{{ t("starting-price-abbreviation") }}</span>
								<span v-else> {{ t("starting-price") }}</span>
							</div>
							<UiFormToggle label="" v-model="isStartingPrice" />
						</div>
					</div>
				</div>
			</div>
		</div>
		<TimeForm
			v-if="ukConfig?.Race && ukConfig.Race.TimeFormPreviewTypeLong"
			:race="ukConfig?.Race"
			:racers="ukConfig.Racers"
			:outcomes="ukConfig.Outcomes"
		/>
		<div class="mt-2 overflow-hidden rounded-lg">
			<Outright v-if="isOutright" :is-loading="isFetching || isLoading" />
			<template v-else>
				<div
					v-if="
						selectedEvent?.result &&
						selectedEvent?.result?.marketGroups.length > 0 &&
						selectedEvent?.result &&
						selectedEvent?.result?.marketGroups.some((m) => m.groupName !== 'Ante Post')
					"
					class="h-9 mb-2"
				>
					<MarketNav
						v-if="selectedEvent?.result.events[0].isActive"
						@refetch-event="handleEventRefetch($event)"
						:market-groups="selectedEvent?.result.marketGroups"
						:is-event-active="event.isActive"
						:selectedEventId="event.eventId"
					/>
					<div
						v-else-if="!isLoading"
						class="px-2 flex items-center justify-center whitespace-nowrap text-xs transition-all cursor-pointer w-20 rounded-md h-full bg-dark-800 dark:bg-identity text-light-50 more-market-active font-bold"
					>
						{{ t("race-results") }}
					</div>
				</div>

				<MultiMarketsOutcomes
					:selectedEventId="selectedEventId"
					:is-event-finished="event.isFinished || !event.isActive"
					:is-loading="isLoading || isFetching"
					:isStartingPrice="isStartingPrice"
				/>
			</template>
		</div>
	</div>
</template>
